import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Map, { Marker, Popup, NavigationControl, Source, Layer, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { fetchVehiclePositions, type RealtimeVehiclePosition, type RealtimeMode } from '@/services/gtfs-realtime';
import { fetchTripDelays } from '@/services/trip-updates';
import { formatDelayText } from '@/services/route-labels';
import { searchStops } from '@/services/stop-search';
import './LiveMapScreen.css';
import { DARK_MAP_STYLE } from '@/services/map-style';
import { inferVehicleRoute, canonicalRoute, loadRouteShapes, inferVehicleBearing } from '@/services/route-patterns';
import { VehicleMarker, vehicleDisplayLabel, vehicleModeForRoute } from '@/components/VehicleMarker';
import { RefreshCountdown } from '@/components/RefreshCountdown';
import { canPoll } from '@/services/polling';

interface LiveVehicle extends RealtimeVehiclePosition {
  mode: string;
  delay?: number;
  delayLoaded?: boolean;
  feedMode: RealtimeMode;
  publicRoute?: string;
  destination?: string;
}

/** A GeoJSON feature set for a route line (one feature per direction). */
interface RouteLineGroup {
  route: string;
  color: string;
  features: { type: 'Feature'; properties: Record<string, string>; geometry: { type: 'LineString'; coordinates: [number, number][] } }[];
}

const SYDNEY_CENTER = { latitude: -33.8688, longitude: 151.2093 };
const DEFAULT_ZOOM = 13;

// Dark map tiles — using OpenStreetMap raster tiles (reliable, no API key needed)
const MODE_COLORS: Record<string, string> = {
  Train: '#F6891F', Bus: '#0072C6', Ferry: '#61B255',
  Metro: '#0099CD', 'Light Rail': '#D81E34',
};

const BADGE_LETTER: Record<string, string> = {
  Train: 'T', Bus: 'B', Ferry: 'F', Metro: 'M', 'Light Rail': 'L',
};

const VEHICLE_ICONS: Record<string, string> = {
  Train: '🚆', Bus: '🚌', Ferry: '⛴', Metro: '🚇', 'Light Rail': '🚋',
};

const POLL_MODES: Array<{ mode: RealtimeMode; label: string }> = [
  { mode: 'sydneytrains', label: 'Train' },
  { mode: 'metro', label: 'Metro' },
  { mode: 'buses', label: 'Bus' },
  { mode: 'ferries', label: 'Ferry' },
  { mode: 'lightrail', label: 'Light Rail' },
];

function guessMode(mode: RealtimeMode): string {
  switch (mode) {
    case 'sydneytrains': return 'Train';
    case 'metro': return 'Metro';
    case 'buses': return 'Bus';
    case 'ferries': return 'Ferry';
    case 'lightrail': case 'innerwest': return 'Light Rail';
    default: return 'Transit';
  }
}

export function LiveMapScreen() {
  const [searchParams] = useSearchParams();
  const focusLat = searchParams.get('lat');
  const focusLon = searchParams.get('lon');
  const focusStop = searchParams.get('stop');

  const initialCenter = focusLat && focusLon
    ? { latitude: parseFloat(focusLat), longitude: parseFloat(focusLon) }
    : SYDNEY_CENTER;
  const initialZoom = focusLat ? 15 : DEFAULT_ZOOM;

  const [viewState, setViewState] = useState({ ...initialCenter, zoom: initialZoom });
  const [vehicles, setVehicles] = useState<LiveVehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<LiveVehicle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [enabledModes, setEnabledModes] = useState<Set<string>>(
    new Set(['Train', 'Metro', 'Bus', 'Ferry', 'Light Rail']),
  );
  const mapRef = useRef<MapRef | null>(null);
  const geoAttempted = useRef(false);
  const routeInferenceCache = useRef(new globalThis.Map<string, { route?: string; destination?: string }>());
  const bearingCache = useRef(new globalThis.Map<string, number>());
  const [visibleBounds, setVisibleBounds] = useState<[number, number, number, number] | null>(null);
  const [routeLines, setRouteLines] = useState<RouteLineGroup[]>([]);
  const loadedRouteLines = useRef(new Set<string>());

  const moveMap = useCallback((latitude: number, longitude: number, zoom: number) => {
    setViewState({ latitude, longitude, zoom });
    mapRef.current?.flyTo({ center: [longitude, latitude], zoom, duration: 500 });
  }, []);

  const updateVisibleBounds = useCallback(() => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds) return;
    const lonPadding = (bounds.getEast() - bounds.getWest()) * 0.15;
    const latPadding = (bounds.getNorth() - bounds.getSouth()) * 0.15;
    setVisibleBounds([
      bounds.getWest() - lonPadding,
      bounds.getSouth() - latPadding,
      bounds.getEast() + lonPadding,
      bounds.getNorth() + latPadding,
    ]);
  }, []);

  // Geolocation: non-blocking, fire-and-forget. Default is always Sydney.
  useEffect(() => {
    if (geoAttempted.current || focusLat) return;
    geoAttempted.current = true;
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          moveMap(pos.coords.latitude, pos.coords.longitude, 14);
        },
        () => { /* denied — stay on Sydney, do nothing */ },
        { timeout: 5000, enableHighAccuracy: false },
      );
    }
  }, [focusLat, moveMap]);

  // Center on stop when opened via ?stop=ID (from Map button)
  useEffect(() => {
    if (!focusStop) return;
    (async () => {
      try {
        const results = await searchStops(focusStop);
        if (results[0]?.latitude && results[0]?.longitude) {
          moveMap(results[0].latitude, results[0].longitude, 14);
        }
      } catch { /* ignore */ }
    })();
  }, [focusStop, moveMap]);

  const loadVehicles = useCallback(async () => {
    if (!canPoll()) return;
    setLoading(true);
    setError('');
    const enabledFeeds = POLL_MODES.filter(({ label }) => enabledModes.has(label));
    const feedResults = await Promise.all(enabledFeeds.map(async ({ mode }) => {
      try {
        const positions = await fetchVehiclePositions(mode);
        return { mode, positions };
      } catch (err) {
        console.warn(`Vehicle fetch failed for ${mode}:`, err);
        return null;
      }
    }));

    const baseVehicles: LiveVehicle[] = feedResults.flatMap((result) => {
      if (!result) return [];
      return result.positions.filter(pos => pos.latitude && pos.longitude).map((pos) => ({
        ...pos,
        mode: vehicleModeForRoute(guessMode(result.mode), pos.route_id),
        feedMode: result.mode,
      }));
    });

    setVehicles(baseVehicles);
    setLastUpdate(new Date());
    setLoading(false);

    // Draw the rail/metro/light-rail network lines for routes that currently
    // have vehicles. Only load shapes for routes we haven't already drawn.
    const railModes = new Set(['Train', 'Metro', 'Light Rail']);
    const railRoutes = new Set<string>();
    for (const v of baseVehicles) {
      if (!railModes.has(v.mode)) continue;
      const route = canonicalRoute(v.route_id);
      if (route && !loadedRouteLines.current.has(route)) railRoutes.add(route);
    }
    if (railRoutes.size) {
      const shapes = await loadRouteShapes([...railRoutes]);
      const newGroups: RouteLineGroup[] = [];
      for (const [route, paths] of shapes) {
        if (loadedRouteLines.current.has(route)) continue;
        loadedRouteLines.current.add(route);
        const mode = baseVehicles.find(v => canonicalRoute(v.route_id) === route)?.mode || 'Train';
        newGroups.push({
          route,
          color: MODE_COLORS[mode] || '#F6891F',
          features: paths.map(path => ({
            type: 'Feature' as const,
            properties: { route },
            geometry: { type: 'LineString' as const, coordinates: path },
          })),
        });
      }
      if (newGroups.length) setRouteLines(current => [...current, ...newGroups]);
    }

    // Infer a direction arrow for rail vehicles whose feed omits a bearing
    // (the Sydney Trains feed does). Cache by route+rounded coords.
    const needBearing = baseVehicles.filter(v => !Number.isFinite(v.bearing) && railModes.has(v.mode));
    if (needBearing.length) {
      const inferred = new globalThis.Map<string, number>();
      for (const v of needBearing) {
        const key = `${canonicalRoute(v.route_id)}:${v.latitude.toFixed(3)}:${v.longitude.toFixed(3)}`;
        const cached = bearingCache.current.get(key);
        if (cached != null) { inferred.set(v.vehicle_id || v.trip_id, cached); continue; }
        const bearing = await inferVehicleBearing(v.route_id, [v.longitude, v.latitude]).catch(() => undefined);
        if (bearing != null) { bearingCache.current.set(key, bearing); inferred.set(v.vehicle_id || v.trip_id, bearing); }
      }
      if (inferred.size) {
        setVehicles(current => current.map(v => {
          const b = inferred.get(v.vehicle_id || v.trip_id);
          return b != null ? { ...v, bearing: b } : v;
        }));
      }
    }
  }, [enabledModes]);

  useEffect(() => {
    void loadVehicles();
    intervalRef.current = setInterval(() => { void loadVehicles(); }, 30000);
    const resume = () => {
      if (canPoll()) void loadVehicles();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [loadVehicles]);

  const toggleMode = (label: string) => {
    setEnabledModes((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const selectVehicle = useCallback(async (vehicle: LiveVehicle) => {
    setSelectedVehicle(vehicle);

    const delayPromise = vehicle.delayLoaded
      ? Promise.resolve(vehicle.delay)
      : fetchTripDelays(vehicle.feedMode).then(delays => delays.get(vehicle.trip_id)).catch(() => undefined);

    const routePromise = vehicle.destination
      ? Promise.resolve({ route: vehicle.publicRoute, destination: vehicle.destination })
      : (async () => {
          const cacheKey = `${vehicle.mode}:${vehicle.route_id}:${vehicle.latitude.toFixed(2)}:${vehicle.longitude.toFixed(2)}`;
          let route = routeInferenceCache.current.get(cacheKey);
          if (!route) {
            route = await inferVehicleRoute(vehicle.route_id || '', [vehicle.longitude, vehicle.latitude], vehicle.mode).catch(() => undefined);
            if (route) routeInferenceCache.current.set(cacheKey, route);
          }
          return route;
        })();

    const [delay, route] = await Promise.all([delayPromise, routePromise]);
    const enriched = {
      ...vehicle,
      delay,
      delayLoaded: true,
      publicRoute: route?.route || vehicle.publicRoute,
      destination: route?.destination || vehicle.destination,
    };
    setSelectedVehicle(current => current === vehicle ? enriched : current);
    setVehicles(current => current.map(item => item === vehicle ? enriched : item));
  }, []);

  const visibleVehicles = useMemo(() => {
    if (!visibleBounds) return vehicles;
    const [west, south, east, north] = visibleBounds;
    return vehicles.filter(vehicle => vehicle.longitude >= west && vehicle.longitude <= east && vehicle.latitude >= south && vehicle.latitude <= north);
  }, [vehicles, visibleBounds]);

  return (
    <main className="page-container map-page">
      <h1 className="page-title">Live Map</h1>

      {/* Mode toggles */}
      <div className="map-mode-toggles">
        {POLL_MODES.map(({ label }) => {
          const isOn = enabledModes.has(label);
          const color = MODE_COLORS[label] || '#666';
          return (
            <button
              key={label}
              className={`map-mode-toggle${isOn ? ' map-mode-toggle--on' : ''}`}
              style={isOn ? { borderColor: color } : {}}
              onClick={() => toggleMode(label)}
            >
              <span className="map-mode-badge" style={{ background: color, opacity: isOn ? 1 : 0.35 }}>{BADGE_LETTER[label] || label[0]}</span>
              {label}
            </button>
          );
        })}
      </div>

      <div className="map-controls">
        <span className="map-stats">
          {vehicles.length > 0 ? `${vehicles.length} vehicles tracking` : 'No vehicles loaded'}
          {lastUpdate && ` • ${lastUpdate.toLocaleTimeString()}`}
        </span>
        {loading && <span className="map-loading">Refreshing...</span>}
        {error && <span className="map-error">{error}</span>}
        <RefreshCountdown lastUpdate={lastUpdate} loading={loading} />
      </div>

      <div className="map-container">
        <Map
          ref={mapRef}
          initialViewState={viewState}
          onLoad={updateVisibleBounds}
          onMoveEnd={updateVisibleBounds}
          mapStyle={DARK_MAP_STYLE}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          attributionControl={false}
        >
          <NavigationControl position="top-right" />

          {/* Rail/metro/light-rail network lines, coloured by mode */}
          {routeLines.map((group) => (
            <Source key={group.route} id={`route-line-${group.route}`} type="geojson" data={{ type: 'FeatureCollection', features: group.features }}>
              <Layer
                id={`route-line-layer-${group.route}`}
                type="line"
                paint={{ 'line-color': group.color, 'line-width': 2.5, 'line-opacity': 0.55 }}
              />
            </Source>
          ))}

          {visibleVehicles.map((v) => (
            <Marker
              // Vehicle ids are not globally unique across TfNSW feeds (and
              // can be absent), so include the feed-normalised mode and
              // coordinates to prevent React reusing the wrong marker/closure
              // when feeds refresh or overlap.
              key={`${v.mode}:${v.vehicle_id || v.trip_id || 'vehicle'}:${v.latitude}:${v.longitude}`}
              latitude={v.latitude}
              longitude={v.longitude}
              // Keep live vehicles above the stop/label layers.  Within the
              // vehicle layer give the less-dense modes a small priority so a
              // light-rail or ferry marker remains clickable when it shares a
              // pixel with a bus marker at an interchange.
              style={{ zIndex: v.mode === 'Ferry' ? 40 : v.mode === 'Light Rail' ? 38 : v.mode === 'Metro' ? 36 : v.mode === 'Train' ? 34 : 30 }}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                void selectVehicle(v);
              }}
            >
              <VehicleMarker
                mode={v.mode}
                route={v.publicRoute || v.route_id}
                destination={v.destination}
                bearing={v.bearing}
                title={`${vehicleDisplayLabel(v.mode, v.publicRoute || v.route_id, v.destination)}${v.delay != null ? ` — ${formatDelayText(v.delay)}` : ''}`}
              />
            </Marker>
          ))}

          {selectedVehicle && (
            <Popup
              latitude={selectedVehicle.latitude}
              longitude={selectedVehicle.longitude}
              anchor="bottom"
              onClose={() => setSelectedVehicle(null)}
              offset={20}
            >
              <div className="vehicle-popup">
                <strong>{vehicleDisplayLabel(selectedVehicle.mode, selectedVehicle.publicRoute || selectedVehicle.route_id, selectedVehicle.destination)}</strong>
                {!selectedVehicle.delayLoaded ? (
                  <p>Loading delay…</p>
                ) : selectedVehicle.delay != null ? (
                  <p>{formatDelayText(selectedVehicle.delay)}</p>
                ) : (
                  <p>No delay info</p>
                )}
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </main>
  );
}

